import React, { useEffect, useState } from "react";
import { adminFetchTickets, adminFetchTicket, adminReplyTicket, adminSetTicketStatus } from "../services/api";

// Admin support queue — the founder / technical team sees every hotel's tickets
// and replies. Cross-tenant; gated to ADMIN_EMAILS on the backend.
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: 9, borderRadius: 8, border: "1px solid #d8dee2", fontSize: 14 };
const badge = (s) => ({ submitted: "#0C447C", received: "#0C447C", investigating: "#854F0B", waiting_customer: "#6D28D9", solution_provided: "#1D7A44", resolved: "#1D7A44", closed: "#8a949b", open: "#0C447C", in_progress: "#854F0B" }[s] || "#8a949b");
const sevColor = (s) => ({ critical: "#A32D2D", urgent: "#A32D2D", high: "#854F0B", normal: "#5b6472", low: "#8a949b" }[s] || "#5b6472");
const STATUSES = ["submitted", "received", "investigating", "waiting_customer", "solution_provided", "resolved", "closed"];

export default function AdminSupportPage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => adminFetchTickets(filter).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openThread = async (id) => {
    setOpenId(id); setThread(null); setReply("");
    try { setThread(await adminFetchTicket(id)); } catch { setThread(null); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { const d = await adminReplyTicket(openId, reply.trim()); setReply(""); setThread(d); load(); }
    catch { /* ignore */ } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try { const d = await adminSetTicketStatus(openId, status); setThread(d); load(); } catch { /* ignore */ }
  };

  if (openId && thread) {
    return (
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button onClick={() => { setOpenId(null); setThread(null); }} style={{ background: "none", border: "none", color: "#0F6E56", cursor: "pointer", fontSize: 14, padding: 0 }}>← All tickets</button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 2px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{thread.subject}</h1>
          <span style={{ fontSize: 12, fontWeight: 700, color: badge(thread.status), textTransform: "uppercase" }}>{thread.status}</span>
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
          <b>{thread.ticket_number}</b> · {thread.hotel && thread.hotel.name} · {thread.created_by_name || "—"} · {thread.affected_module || thread.category} · <span style={{ color: sevColor(thread.priority || thread.severity) }}>{thread.priority || thread.severity}</span>
          {thread.reference_number ? <> · #{thread.reference_number}</> : null}
          {thread.context_path ? <> · <code style={{ fontSize: 12 }}>{thread.context_path}</code></> : null}
        </div>
        <div style={{ display: "flex", gap: 6, margin: "8px 0 12px", flexWrap: "wrap" }}>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={"btn small " + (thread.status === s ? "primary" : "secondary")}>{s}</button>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(thread.messages || []).map((m) => (
            <div key={m.id} style={{ ...CARD, padding: "10px 14px", alignSelf: m.author_type === "support" ? "flex-end" : "flex-start", maxWidth: "85%", background: m.author_type === "support" ? "#E1F5EE" : "#fff" }}>
              <div style={{ fontSize: 12, color: "#5b6472", marginBottom: 3 }}>{m.author_type === "support" ? "ODRA Support" : (m.author_name || "Hotel")} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
              <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{m.body}</div>
            </div>
          ))}
        </div>
        <div style={{ ...CARD, padding: 12, marginTop: 14 }}>
          <textarea style={{ ...inp, width: "100%", minHeight: 80, resize: "vertical" }} placeholder="Reply to the hotel…" value={reply} onChange={(e) => setReply(e.target.value)} />
          <button className="btn primary small" disabled={busy} onClick={sendReply} style={{ marginTop: 8 }}>Send reply</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>Support queue</h1>
      <div className="muted" style={{ fontSize: 14 }}>Incidents and questions reported by hotels</div>
      <div style={{ display: "flex", gap: 6, margin: "14px 0" }}>
        <button onClick={() => setFilter("")} className={"btn small " + (filter === "" ? "primary" : "secondary")}>All</button>
        {STATUSES.map((s) => <button key={s} onClick={() => setFilter(s)} className={"btn small " + (filter === s ? "primary" : "secondary")}>{s}</button>)}
      </div>
      {rows == null ? <p className="muted">…</p> : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, textAlign: "center" }} className="muted">No tickets.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((tk) => (
            <div key={tk.id} onClick={() => openThread(tk.id)} style={{ ...CARD, padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{tk.subject} <span style={{ fontSize: 12, color: sevColor(tk.severity), fontWeight: 700 }}>· {tk.severity}</span></div>
                <div className="muted" style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tk.ticket_number} · {tk.hotel && tk.hotel.name} · {tk.affected_module || tk.category} · {tk.last_message || ""}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: badge(tk.status), textTransform: "uppercase" }}>{tk.status}</div>
                <div className="muted" style={{ fontSize: 11 }}>{tk.updated_at ? new Date(tk.updated_at).toLocaleDateString() : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
